import { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
/*
 * Route-driven state container for a singular Project
 */
import type { FunctionComponent } from 'react';

import { useParams } from '../../contexts/RouterContext';
import { useCurrentProject } from '../../hooks/api/projects/useCurrentProject';
import NavigationStore from '../../redux/navigation';
import utils, { getProjectBaseURLFromParams } from '../../utils';
import Loader from '../generic/Loader';
import NotFound from '../generic/NotFound';

import { ProjectProvider } from './contexts/ProjectContext';
import DisplayProjectNav from './DisplayProjectNav';
import ProjectEmpty from './ProjectEmpty';
import { getLatestRevisionIdFromProject } from './shared/helpers/getLatestRevisionIdFromProject';
import ProjectSubnav from './subnav/ProjectSubnav';
import type { RevisionRouteParams } from './projectDashboardTypes';

/**
 * This is typed (probably incorrectly) as `Error | null` in our useQuery.
 * This function just helps determine if this error is a 404 or NotFoundError.
 */
const isNotFoundError = (error: unknown) => {
  //  Case where the error is a Response object with a 404 status (most likely)
  if ((error as Response)?.status === 404) {
    return true;
  }
  if ((error as Response)?.statusText.toLowerCase().includes('not found')) {
    return true;
  }

  //  Handle case where the error is a string that contains "not found" or "no project found"
  if (
    (error as Error)?.message.toLowerCase().includes('no project found') ||
    (error as Error)?.message.toLowerCase().includes('not found')
  ) {
    return true;
  }

  return false;
};

/**
 * Loader component for the ProjectContainer2 component.
 */
const ProjectContainerLoader = () => (
  <div className="project-container container margin-top">
    <div className="col-sm-8">
      <div className="panel padded">
        <Loader />
      </div>
    </div>
  </div>
);

/**
 * Container component for viewing a project.
 * This component fetches the project via a React Query hook, and
 * then makes the projecct data available via a React Context.
 */
export const ProjectContainer: FunctionComponent = ({ children }) => {
  const params = useParams<RevisionRouteParams>();
  const dispatch = useDispatch();
  //  This hook looks at the URL params to determine which project to fetch.
  const { data: project, isLoading, isFetching, error } = useCurrentProject();

  //  Using a memo here basically just for the closure/to keep the branching logic clean.
  const {
    baseProjectURL,
    baseRevisionURL,
    revisionId,
  } = useMemo<{ baseProjectURL: string; baseRevisionURL: string; revisionId: string | null }>(() => {
    const projectBaseURL = `/projects/${utils.nuc(params.project_id)}`;
    const revId = params.revision_id || getLatestRevisionIdFromProject(project);

    if (isFetching) {
      return {
        baseProjectURL: projectBaseURL,
        baseRevisionURL: projectBaseURL,
        revisionId: revId,
      };
    }

    if (!revId) {
      return {
        baseProjectURL: projectBaseURL,
        baseRevisionURL: getProjectBaseURLFromParams(params),
        revisionId: revId,
      };
    }

    return {
      baseProjectURL: projectBaseURL,
      baseRevisionURL: getProjectBaseURLFromParams({
        ...params,
        reference_type: params.reference_type || 'branch',
        reference: project?.default_branch ?? 'master',
        revision_id: revId,
      }),
      revisionId: revId,
    };
  }, [params, project, isFetching]);

  // This ensures that the DisplayProjectNav component is updated when the project data changes.
  useEffect(() => {
    dispatch(NavigationStore.actions.setNavState({
      expand: 'project-expanded',
      subnav: DisplayProjectNav,
      navProps: {
        //  Technically we aren't using the CallToAction component, but this gets rid of the border-bottom for us.
        shouldUseCallToAction: true,
      },
    }));
  }, [dispatch, project]);

  const projectProviderValue = useMemo(() => ({
    data: project,
    status: { loading: isLoading, error: error ? error.message : undefined },
  }), [project, isLoading, error]);

  //  Don't even render the ProjectCTA/Subnav if the project isn't found.
  if (isNotFoundError(error)) {
    return <NotFound />;
  }

  // TODO: this is for TS, but we should render something here
  if (!project && !isFetching) {
    return <ProjectEmpty />;
  }

  return (
    <ProjectProvider value={projectProviderValue}>
      <ProjectSubnav
        projectOrgId={project?.organizationId ?? 0}
        baseProjectURL={baseProjectURL}
        baseRevisionURL={baseRevisionURL}
        projectLocator={params.project_id}
        revisionId={revisionId}
      />
      {isLoading ? <ProjectContainerLoader /> : (
        <div className="project-container">
          {children}
        </div>
      )}
    </ProjectProvider>
  );
};
